import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  model,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatRadioModule } from '@angular/material/radio';
import { TextareaWithSpeechToTextFormFieldComponent } from '../../../../shared/components/textarea-with-speech-to-text-form-field/textarea-with-speech-to-text-form-field.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { MultiSelectSearchFormFieldComponent } from '../../../../shared/components/multi-select-search-form-field/multi-select-search-form-field.component';
import { MatListModule } from '@angular/material/list';
import { IDataDiagnosticos } from '../../../../shared/interfaces/diagnosticos.interface';
import { ServiceFormDiagnosticoService } from '../../../../shared/services/service-form-diagnostico.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModalErrorGeneralComponent } from '../../../common/modal-error-general/modal-error-general.component';
import { DiagnosticoService } from '../../../services/diagnostico.service';
import {
  SnackInfoComponent,
  SnackInfoProps,
  SnackInfoType,
} from '../../../../shared/components/snack-info/snack-info.component';
import { IDataDiagnostico } from '../../../../interfaces/diagnostico.interface';
import { FichaClinicaStore } from '../../../../shared/stores/ficha-clinica/ficha-clinica.store';
import { fichaClinicaFinalizada } from '../../../../shared/helpers/ficha-clinica.helper';
import { AppPermissions, ValidRoles } from '../../../../shared/enums';
import { HasAccessDirective } from '../../../../shared/directives/has-access/has-access.directive';
import { AuthenticationService } from '../../../services/authentication.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FinishAppointmentButtonComponent } from '../../../common/components/finish-appointment-button/finish-appointment-button.component';
import {
  ModalConfirmComponent,
  ModalConfirmProps,
  ModalConfirmType,
} from '../../../../shared/components/modal-confirm/modal-confirm.component';

interface DiagnosticoForm {
  tipoDiagnostico: FormControl<string | null>;
  diagnosticos: FormControl<Array<string> | null>;
  observacion: FormControl<string | null>;
}

@Component({
  selector: 'soph-diagnostico',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatCheckboxModule,
    MatListModule,
    TextareaWithSpeechToTextFormFieldComponent,
    MultiSelectSearchFormFieldComponent,
    HasAccessDirective,
    MatProgressSpinnerModule,
    FinishAppointmentButtonComponent,
  ],
  templateUrl: './diagnostico.component.html',
  styleUrl: './diagnostico.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticoComponent {
  public readonly formReady = model(false);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  protected form: FormGroup<DiagnosticoForm>;
  protected diagnoses = signal<Array<IDataDiagnosticos>>([]);
  protected readonly tipoDiagnositico = signal<string | null>(null);
  protected readonly selectedValuesTag: WritableSignal<Array<any>> = signal([]);
  private readonly wasSavedOnce = signal<boolean>(false);
  private readonly diagnostico = signal<IDataDiagnostico | null>(null);
  private readonly auth: AuthenticationService = inject(AuthenticationService);
  private readonly hasChanges = signal<boolean>(false);
  protected readonly isSavingData = signal<boolean>(false);
  protected readonly isAppointmentFinished = signal<boolean>(false);

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _diagnosticoDataService: ServiceFormDiagnosticoService,
    private readonly _diagnosticoService: DiagnosticoService,
    private readonly _fichaClinicaStore: FichaClinicaStore,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this.form = this._fb.group({
      tipoDiagnostico: new FormControl<string | null>(
        null,
        Validators.compose([Validators.required])
      ),
      diagnosticos: new FormControl<Array<string> | null>(
        null,
        Validators.compose([Validators.required])
      ),
      observacion: new FormControl<string | null>(
        null,
        Validators.compose([Validators.maxLength(1000)])
      ),
    });

    this.form.valueChanges.pipe(distinctUntilChanged()).subscribe((newValues) => {
      this.hasChanges.set(this.checkForChanges(newValues));
    });

    this.form
      .get('diagnosticos')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((values) => {
        if (values) {
          this.selectedValuesTag.set(
            this.diagnoses().filter((item) => values.includes(item.idDiagnostico))
          );
        }
      });

    effect(
      () => {
        if (this.wasSavedOnce() && !this.hasChanges() && this.form.valid) {
          this._fichaClinicaStore.updateMedicalHistoryBySection('diagnostico', true);
          this.formReady.set(true);
        }

        this._fichaClinicaStore.hasUnsavedChanges(this.hasChanges());
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.getData();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  isBtnGuardarDisabled(): boolean {
    return this.form.invalid || !this.hasChanges();
  }

  private toggleFormState(shouldDisable: boolean) {
    shouldDisable || !this.auth.hasRole(ValidRoles.MEDIC)
      ? this.form.disable()
      : this.form.enable();

    this._cdr.detectChanges();
  }

  public onRadioButtonClick(value: string) {
    if (!this.form.controls['tipoDiagnostico'].disabled) {
      if (!!this.tipoDiagnositico() && this.tipoDiagnositico() !== value) {
        this.selectedValuesTag.set([]);
        this.form.reset();
        this.form.updateValueAndValidity();
      }

      this.tipoDiagnositico.set(value);
      this.form.get('tipoDiagnostico')?.setValue(this.tipoDiagnositico());
    }
  }

  public eliminarDiagnosticoSeleccionado(id: string) {
    const selectedValues = this.form.get('diagnosticos')?.value || [];
    const filteredValues = selectedValues?.filter((value) => value !== id);
    this.form.get('diagnosticos')?.setValue(filteredValues);
  }

  public onSave(form: FormGroup) {
    if (form.valid) {
      this.isSavingData.set(true);

      const { value } = form;

      this._diagnosticoService
        .guardar({
          ...value,
          idAgendamiento: this._fichaClinicaStore.values.idAgendamiento,
        })
        .subscribe({
          next: () => {
            this.wasSavedOnce.set(true);
            this.diagnostico.set({
              ...value,
            });

            this.form.updateValueAndValidity();

            this._fichaClinicaStore.updateMedicalHistoryBySection('diagnostico', true);
            this.formReady.set(true);

            this.snackBar.openFromComponent(SnackInfoComponent, {
              data: {
                message: 'Los cambios han sido guardados con éxito',
                type: SnackInfoType.SUCCESS,
              } as SnackInfoProps,
            });

            this.isSavingData.set(false);

            this._fichaClinicaStore.changeTabSelected(3);
          },
          error: () => {
            this.snackBar.openFromComponent(SnackInfoComponent, {
              data: {
                message: 'No pudimos guardar los cambios, por favor intenta nuevamente',
                type: SnackInfoType.ERROR,
              } as SnackInfoProps,
            });

            this.isSavingData.set(false);
          },
        });
    } else {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });

      this.snackBar.openFromComponent(SnackInfoComponent, {
        data: {
          message: 'Faltan completar campos requeridos.',
          type: SnackInfoType.WARNING,
        },
        duration: 3000,
      });
    }
  }

  protected openRevertChangesConfirmation() {
    const dialogRef = this.dialog.open(ModalConfirmComponent, {
      autoFocus: false,
      panelClass: 'custom-confirmation-dialog',
      width: '452px',
      data: {
        type: ModalConfirmType.WARNING,
        description: '¿Confirmas que deseas cancelar sin guardar?',
        config: {
          buttons: [
            { title: 'Volver', action: false },
            { title: 'Confirmar', action: true },
          ],
        },
      } as ModalConfirmProps,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.revertChanges();
      }
    });
  }

  public revertChanges() {
    if (this.diagnostico()) {
      this.form.patchValue({
        ...this.diagnostico(),
      });
      this.tipoDiagnositico.set(this.diagnostico()!.tipoDiagnostico);
    } else {
      this.form.reset();
      this.tipoDiagnositico.set(null);
    }
  }

  private checkForChanges(newValues: any): boolean {
    if (this.diagnostico()) {
      return JSON.stringify(newValues) !== JSON.stringify(this.diagnostico());
    } else {
      const defaultValue = {
        tipoDiagnostico: null,
        diagnosticos: null,
        observacion: null,
      };

      return JSON.stringify(defaultValue) !== JSON.stringify(newValues);
    }
  }

  private getData() {
    this._diagnosticoDataService.getDiagnosticos().subscribe((data) => {
      this.diagnoses.set(data);

      if (this._fichaClinicaStore.values.idAgendamiento) {
        this.getDiagnostico();
      }
    });
  }

  private getDiagnostico() {
    this._diagnosticoService.obtener(this._fichaClinicaStore.values.idAgendamiento!).subscribe({
      next: (response) => {
        if (response.data) {
          this.wasSavedOnce.set(true);
          this.form.reset();

          this.diagnostico.set(response.data);

          this.tipoDiagnositico.set(response.data.tipoDiagnostico);

          this.selectedValuesTag.set(
            this.diagnoses().filter((item) =>
              response.data.diagnosticos.includes(item.idDiagnostico)
            )
          );

          this.form.patchValue({
            ...response.data,
          });

          this._cdr.detectChanges();

          const isFormValid = this.form.valid;
          this._fichaClinicaStore.updateMedicalHistoryBySection('diagnostico', isFormValid);
          this.formReady.set(isFormValid);
        }

        const fichaClinicaStore$ = this._fichaClinicaStore.state$.subscribe((values) => {
          const isAppointmentFinished = fichaClinicaFinalizada(values.estado || '');
          this.toggleFormState(isAppointmentFinished);
          this.isAppointmentFinished.set(isAppointmentFinished);
        });
        this.subscriptions.push(fichaClinicaStore$);
      },
      error: () => {
        this.dialog.open(ModalErrorGeneralComponent, {
          width: '452px',
          disableClose: true,
        });
      },
    });
  }

  protected readonly AppPermissions = AppPermissions;
}
